import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { SITE } from "../constants/site";
import { Trash2, Mail, AlertTriangle } from "lucide-react";

export default function AccountDeletion() {
    return (
        <div
            data-testid="account-deletion-page"
            className="min-h-screen bg-background"
        >
            <Navbar />

            <section className="pt-32 pb-12 border-b border-slate-200 bg-white">
                <div className="max-w-4xl mx-auto px-6 sm:px-8">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700">
                        <Trash2 className="h-4 w-4 text-brand-coral" />
                        DATA CONTROL
                    </div>
                    <h1 className="font-display mt-5 text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
                        Account &amp; Data Deletion
                    </h1>
                    <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
                        {SITE.appName} by {SITE.company} · Republic of South
                        Africa
                    </p>
                </div>
            </section>

            <section className="py-16">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 space-y-12">
                    {/* In-app deletion */}
                    <div
                        data-testid="section-in-app-deletion"
                        className="rounded-2xl border border-slate-200 bg-white p-8"
                    >
                        <div className="flex items-center gap-3">
                            <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-slate-900 text-white">
                                <Trash2 className="h-5 w-5" />
                            </div>
                            <h2 className="font-display text-2xl font-bold text-slate-900">
                                Delete your account inside the app
                            </h2>
                        </div>
                        <ol className="mt-6 space-y-3 text-slate-700">
                            <li className="flex gap-3">
                                <span className="mt-0.5 h-6 w-6 flex items-center justify-center rounded-full brand-gradient text-white text-xs font-semibold flex-shrink-0">
                                    1
                                </span>
                                Open {SITE.appName} and sign in.
                            </li>
                            <li className="flex gap-3">
                                <span className="mt-0.5 h-6 w-6 flex items-center justify-center rounded-full brand-gradient text-white text-xs font-semibold flex-shrink-0">
                                    2
                                </span>
                                Go to the <strong>More</strong> tab →{" "}
                                <strong>Account &amp; Data</strong>.
                            </li>
                            <li className="flex gap-3">
                                <span className="mt-0.5 h-6 w-6 flex items-center justify-center rounded-full brand-gradient text-white text-xs font-semibold flex-shrink-0">
                                    3
                                </span>
                                Tap <strong>Delete my account</strong>.
                            </li>
                            <li className="flex gap-3">
                                <span className="mt-0.5 h-6 w-6 flex items-center justify-center rounded-full brand-gradient text-white text-xs font-semibold flex-shrink-0">
                                    4
                                </span>
                                Type <strong>DELETE</strong> to confirm.
                            </li>
                        </ol>
                        <p className="mt-6 text-sm text-slate-600">
                            Your account and all associated data are
                            permanently erased.
                        </p>
                    </div>

                    {/* Request by email */}
                    <div
                        data-testid="section-email-deletion"
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-8"
                    >
                        <div className="flex items-center gap-3">
                            <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-brand-purple text-white">
                                <Mail className="h-5 w-5" />
                            </div>
                            <h2 className="font-display text-2xl font-bold text-slate-900">
                                Request deletion by email
                            </h2>
                        </div>
                        <p className="mt-5 text-slate-700 leading-relaxed">
                            If you can no longer access the app, email{" "}
                            <a
                                href={`mailto:${SITE.supportEmail}`}
                                className="text-brand-purple font-medium hover:underline"
                            >
                                {SITE.supportEmail}
                            </a>{" "}
                            from the email address linked to your account with
                            the subject{" "}
                            <em>"Account Deletion Request"</em>. We will verify
                            your identity and delete your account.
                        </p>
                    </div>

                    {/* What gets deleted */}
                    <div
                        data-testid="section-what-deleted"
                        className="rounded-2xl border border-slate-200 bg-white p-8"
                    >
                        <h2 className="font-display text-2xl font-bold text-slate-900">
                            What gets deleted
                        </h2>
                        <p className="mt-4 text-slate-700 leading-relaxed">
                            All personal information: your profile, family
                            member profiles, health records, documents, pet
                            records, assets, staff records, emergency profiles,
                            sharing permissions and audit history.
                        </p>
                    </div>

                    {/* What may be retained */}
                    <div
                        data-testid="section-what-retained"
                        className="rounded-2xl border border-amber-200 bg-amber-50 p-8"
                    >
                        <div className="flex items-center gap-3">
                            <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-amber-500 text-white">
                                <AlertTriangle className="h-5 w-5" />
                            </div>
                            <h2 className="font-display text-2xl font-bold text-slate-900">
                                What may be retained
                            </h2>
                        </div>
                        <p className="mt-5 text-slate-700 leading-relaxed">
                            Billing records required by South African tax law
                            are retained for the legally mandated period, then
                            destroyed. All other data is permanently erased
                            within 30 days of your request.
                        </p>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
